import { Component, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { api_routes, db_tables } from '@app/consts';
import { EmployeeLanguageService } from './services/employee-language.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { EmployeeLanguage } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/components/employee-details/components/employee-languages/models/employee-language.model';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { defer, forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { FormGeneratorDialogComponent } from '@app/shared/components/form-generator-dialog/form-generator-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ActivatedRoute } from '@angular/router';
import { EmployeeTableSecurity } from '@app/shared/models/employee.model';
import { TablePermissionsService } from '@app/core/services/table-permissions/table-permissions.service';

@Component({
  selector: 'app-employee-languages',
  templateUrl: './employee-languages.component.html',
  styleUrls: ['./employee-languages.component.scss']
})
export class EmployeeLanguagesComponent implements OnInit {
  @Input() formIds: string[];
  employeeID: string;

  public columns: any[] = [
    { field: "dialect", subField: "text", title: "dialect", type: "navigation", tableId: "tfi_LanguagesLanguage"},
    { field: "conversationalLevel", subField: "text", title: "conversational Level", tableId: "tfi_ConvLevel"},
    { field: "readingWritingLevel", subField: "text", title: "reading Writing Level", tableId: "tfi_ReadWriteLevel"},
    { field: "dateUpdated", title: "date Updated", type: "date", tableId: "tfi_LanDateUpdated"},
    { field: "comments", title: "comments", tableId: "tfi_LangComments"},
  ];
  public bindingType: String = "array";
  public view: any;
  public gridData: any;
  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean;
  pageSize: number = 5;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  dialogRef: MatDialogRef<FormGeneratorDialogComponent>;
  formId: string = 'frm_wiVZbFOqsnDtZH'
  changeReasonFormId: string = 'frm_slCpAY0ztLNOUA';
  formValid: boolean;
  private sortString: string = 'DialectText-asc';
  isLoadingPermissions: boolean = true;
  targetEmployeeTablePermissions: EmployeeTableSecurity[];
  accessAndHistoryURL: string = `${api_routes.TALENT}/[employeeId]/${api_routes.LANGUAGES}/[entityId]`;

  constructor(
    private dialog: MatDialog,
    private translate: TranslateService,
    private employeeLanguageService: EmployeeLanguageService,
    private snackbarService: SnackbarService,
    private overlayService: OverlayService,
    private tablePermissionsService: TablePermissionsService,
    private route: ActivatedRoute,
  ) {
    this.route.paramMap.subscribe(
        params => this.employeeID = params.get("id")
    )
  }

  ngOnInit(): void {
    this.view = this.getLanguages();
    this.getTargetEmployeeTablePermissions();
  }

  getLanguages() {
    this.isLoading = true;

    this.employeeLanguageService.getLanguages(this.employeeID, this.pageSize, this.skip, this.sortString)
      .pipe(
        finalize( () => {
          this.isLoading = false;
        })
      )
      .subscribe(
        (res) => {
          this.gridDataResult = {
            data: res.data,
            total: res.total,
          }
          this.gridData = res.data;
        }
      );
  }

  getLanguage(language?: EmployeeLanguage) {
    if(language === undefined){
      this.openFormDialog();
    }
    else {
      this.isLoading = true;

      this.employeeLanguageService.getLanguage(this.employeeID, language.id)
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe(
        res => {
          this.openFormDialog(res);
        }
      );
    }
  }

  getTargetEmployeeTablePermissions() {
    this.isLoadingPermissions = true;

    let currentFetchedData = this.tablePermissionsService.getFetchedTargetEmployeeTablePermissions(this.employeeID);

    // if the current table permissions that are saved are for this employee use those, else fetch the new permissions.
    if(currentFetchedData !== null){
      this.targetEmployeeTablePermissions = currentFetchedData;
      this.isLoadingPermissions = false;
    }
    else {
      this.tablePermissionsService.getTargetEmployeeTablePermissions(this.employeeID)
      .pipe(
        finalize(() => {
            this.isLoadingPermissions = false;
        })
      )
      .subscribe(
        res => {
          this.targetEmployeeTablePermissions = res;
        }
      );
    }
  }

  openFormDialog(language?: EmployeeLanguage) {
    let formData = {
      id: language ? language.id : null,
      asOf: moment().format(),
      changeReason: '',
      changeReasonComments: '',
      dateUpdated: language ? language.dateUpdated : null,
      dialect: language ? language.dialect?.id : null,
      conversationalLevel: language ? language.conversationalLevel?.id : null,
      readingWritingLevel: language ? language.readingWritingLevel?.id : null,
      comments: language ? language.comments : null
    };

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      formTitle: `${this.translate.instant('Language')}`,
      formId: this.formId,
      changeReasonFormId: this.changeReasonFormId,
      targetEmployeeTablePermissions: this.targetEmployeeTablePermissions,
      formData: formData
    };

    this.dialogRef = this.dialog.open(FormGeneratorDialogComponent, dialogConfig);

    const sub = this.dialogRef.componentInstance.emitFormData.subscribe((event) => {
      this.save(event)
    });
  }

  formDataEmitted(formData) {
    this.save(formData);
  }

  formStatusUpdated(formValid) {
    this.formValid = formValid;
  }

  save(formData: any){
    this.overlayService.show();
    if (formData.dateUpdated) {formData.dateUpdated = moment(formData.dateUpdated).format().slice(0, 10);}
    if(!formData.id){
      this.employeeLanguageService.createLanguage(this.employeeID, formData)
      .pipe(
        finalize(() => this.overlayService.hide())
      )
      .subscribe(
        (res) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('CreatedSuccessfully')}`, 'clear', 'success');
          this.dialogRef.close(true);
          this.getLanguages();
        }
      );
    }
    else {
      this.employeeLanguageService.updateLanguage(this.employeeID, formData)
      .pipe(
        finalize(() => this.overlayService.hide())
      )
      .subscribe(
        (res) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
          this.dialogRef.close(true);
          this.getLanguages();
        }
      );
    }
  }

  deleteLanguages(languageIds: string[]) {
    const observables = languageIds.map(selectedItem => defer(() => this.employeeLanguageService.deleteLanguage(this.employeeID, selectedItem)));

    this.overlayService.show();

    forkJoin(observables)
    .pipe(
      finalize(() => this.overlayService.hide())
    )
    .subscribe(
      (res) => {
        this.view = this.getLanguages();
        this.clearSelectedItems = !this.clearSelectedItems;
          this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
      },
      err => {
        this.snackbarService.openSnackBar(err, 'clear', 'warn');
      }
    );
  }

  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take
    this.view = this.getLanguages();
  }
}

